import { Component, HostListener, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ITeam } from "@teammood/team";
import { Author, Kudos, Mood, TimelineItemDialogService, Topic } from "./timeline-item";
import { catchError, EMPTY, filter, from, mergeMap, Subject, takeUntil, tap } from "rxjs";
import { TimelineDay, TimelineItemCreation } from "./timeline.types";
import { MoodsService } from "./timeline-item/moods/moods.service";
import { DaysService } from "./daily-overview/days.service";
import { LoggerService } from "@teammood/shared/util/logger";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { UserRequestService } from "./user-requests/user-request.service";
import { DateRange } from "@angular/material/datepicker";
import { DateRangePickerService } from "@teammood/shared/ui/date-range-picker";
import { TimelineService } from "./timeline.service";
import { addDays } from "date-fns";
import { ViewportScroller } from "@angular/common";
import { UserService } from "@teammood/shared/util/user";
import { HttpErrorResponse } from "@angular/common/http";
import { UserRequestBacklog } from "./user-requests/user-request.types";
import { UserInfos } from "../shared/ui/username-icon/user-infos.type";
import { CreatePollService } from "../polls/create-poll/create-poll.service";
import { UserRequestEvent } from "./user-requests/user-requests.component";
import { ActivatedRoute } from "@angular/router";
import { PollSetupService } from "../polls/polls-setup.service";

@Component({
    selector: "timeline",
    templateUrl: "./timeline.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TimelineComponent implements OnInit, OnDestroy {

    teamId: string;
    author: Author;
    busy: boolean;
    userInfos: UserInfos;
    days: TimelineDay[] = [];
    dateRange: DateRange<Date>;
    userRequest: UserRequestBacklog;
    teams: ITeam[];
    isUserManager: boolean;
    filterOptions = [
        {
            id: 'all',
            label: 'timeline.filter.all',
        }, {
            id: 'reviewed',
            label: 'timeline.filter.reviewed',
        }, {
            id: 'not_reviewed',
            label: 'timeline.filter.not_reviewed',
        }, {
            id: 'assigned',
            label: 'timeline.filter.assigned',
        }, {
            id: 'not_assigned',
            label: 'timeline.filter.not_assigned',
        }
    ];
    filterOption: string = 'all';

    protected noMoreItems: boolean = false;
    private _destroy$ = new Subject<void>();
    protected showScrollToTopBtn: 'none' | 'inline-block' = 'none';

    constructor(
        private logger: LoggerService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private timelineItemDialogService: TimelineItemDialogService,
        private moodsService: MoodsService,
        private daysService: DaysService,
        private userRequestService: UserRequestService,
        private dateRangepicker: DateRangePickerService,
        private timelineService: TimelineService,
        private viewportScroller: ViewportScroller,
        private userService: UserService,
        private createPollService: CreatePollService,
        private route: ActivatedRoute,
        private pollSetupService: PollSetupService,
        ) {
    }

    async ngOnInit() {
        this.busy = true;

        this.route.params.pipe(
            filter(params => !!params['teamId']),
            mergeMap(params => {
                this.teamId = params['teamId'];
                this.dateRange = this.dateRangepicker.defaultDateRange("Europe/Paris");
                return this.loadTimelineAndActivity(this.teamId).pipe(
                    tap(() => this.scrollToTimelineItem()),
                    mergeMap(() => {
                        return from(this.checkIfUserRequest());
                    })
                )
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.userService.get().then(async (user) => {
            if (user) {
                this.userInfos = new UserInfos(user.firstname, user.lastname);
                this.author = { id: user.id, fullname: user.fullname };
                this.teams = [...user.teams];
                this.isUserManager = user.roles.includes('manager');
            }
        });
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private scrollToTimelineItem() {
        //if anchor is set, scroll to it (used for notification links)
        const hash = this.route.snapshot.queryParams['focus'];

        if (hash) {
            setTimeout(() => {
                this.viewportScroller.setOffset([0, 112]);
                this.viewportScroller.scrollToAnchor(hash);
            }, 1000);
        }
    }

    private loadTimelineAndActivity(teamId: string) {

        //this.loadFormatFilterActivityData();

        return this.timelineService.timeline(this.dateRange.start, this.dateRange.end, teamId).pipe(
            tap(timeline => {
                this.days = timeline.days;

                this.busy = false;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status !== 404) {
                    this.logger.error(error);
                }
                return EMPTY;
            }),
            takeUntil(this._destroy$)
        );
    }

    async checkIfUserRequest(nextRequestId = null) {
        this.userRequest = await this.userRequestService.nextUserRequest(this.teamId, nextRequestId);
    }

    async reloadToday(newToday: TimelineDay) {
        const today = newToday ? newToday : undefined;
        if (today) {
            if (this.days[0].today) {
                this.days[0] = today;
            } else {
                this.days.unshift(today);
            }
        }
    }

    async onDeleteOrModerateItem() {
        await this.checkIfUserRequest();
    }

    async onCreateTimelineItemAfterRequest(event: UserRequestEvent){
        switch(event.type){
            case 'next': 
                await this.checkIfUserRequest(event.nextRequestId);
                break;
            case 'moodItem': 
                await this.onOpenAddMoodCommentDialog(event.value as Mood);
                await this.checkIfUserRequest(event.nextRequestId);
                break;
            case 'questionItem': 
                await this.checkIfUserRequest(event.nextRequestId);
                break;
            case 'topicItem': 
                await this.checkIfUserRequest(event.nextRequestId);
                break;
            case 'kudosItem': 
                await this.checkIfUserRequest(event.nextRequestId);
                break;
        }
    }

    private async addPoll(question: any) {
        await this.createPollService.createPoll(question);
        if(question._serializePoll){
            this.onOpenCreatePollDialog();
            this.snackBarService.success(this.translateService.instant('question_created'));
        }else{
            const sucessDialog = await this.timelineItemDialogService.questionSuccess();
            sucessDialog.beforeClosed().pipe().subscribe();
        }
    }

    private async addComment(mood: Mood) {
        return this.daysService.updateMood(mood).then(async (response: any) => {
            //save mood then, if user requests, display the next one, else close modal and reload today -> user request by closing modal
            await this.reloadToday(response.day);
            this.snackBarService.success(this.translateService.instant('mood_updated'));
        });
    }

    async notifyAboutTopicCreation(topic: Topic) {
        return await this.daysService.notifyAboutTopicCreation(topic.id).then(() => {
            this.snackBarService.success(this.translateService.instant('topic_created_notify'));
        }).catch((error) => {
            this.logger.error('Error notify about a topic:', error);
            this.snackBarService.error(error);
        });
    }

    private async addTopic(topic: Topic, shouldNotify: boolean) {
        if (topic.title?.body) {
            topic.title.body = this.moodsService.addSpaceBeforeLink(topic.title.body);
            return this.daysService.createTopic(topic, this.teamId).then(async (response: any) => {
                const timelineItemCreation: TimelineItemCreation = response;
                const currentTopic: Topic = timelineItemCreation.event.item as Topic;
                if(shouldNotify) {
                    await this.notifyAboutTopicCreation(currentTopic);
                }
                await this.reloadToday(response.day);
                this.snackBarService.success(this.translateService.instant('topic_created'));
            }).catch((error) => {
                this.logger.error('Error creating topic:', error);
                this.snackBarService.error(error);
            });
        }
    }

    private async addKudos(kudos: Kudos) {
        if (kudos.message?.body) {
            kudos.message.body = this.moodsService.addSpaceBeforeLink(kudos.message.body);
            return this.daysService.createKudos(kudos, this.teamId).then(async (response: any) => {
                await this.reloadToday(response.day);
                this.snackBarService.success(this.translateService.instant('kudos_created'));
            }).catch((error) => {
                this.logger.error('Error creating kudos:', error);
                this.snackBarService.error(error);
            });
        }
    }

    async onOpenAddMoodCommentDialog(moodItem: Mood) {

        const dialogRef = await this.timelineItemDialogService.addMoodComment('MoodRequest', moodItem, this.teamId);

        dialogRef.beforeClosed().pipe(
            tap(async () => {
                // need to check because of mood added even if no comment
                await this.checkIfUserRequest()
            }),
            filter(moodItem => !!moodItem),
            mergeMap(async moodItem => {
                return this.addComment(moodItem)
            }),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    async onOpenCreateKudosDialog() {
        const team: ITeam = {
            id: this.teamId,
            name: ""
        }
        const dialogRef = await this.timelineItemDialogService.createKudos([team], this.author);

        dialogRef.beforeClosed().pipe(
            filter((kudos) => !!kudos),
            tap(kudos => this.addKudos(kudos)),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    async onOpenCreateQuestionDialog(question: string = undefined, templateId: string = undefined) {
        const dialogRef = await this.timelineItemDialogService.createQuestion(this.teamId, question, templateId);

        dialogRef.beforeClosed().pipe(
            filter((topicInfos) => !!topicInfos),
            tap(topicInfos => this.addTopic(topicInfos.topic, topicInfos.notify)),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    async onOpenCreateIssueDialog() {
        const dialogRef = await this.timelineItemDialogService.createIssue(this.teamId);

        dialogRef.beforeClosed().pipe(
            filter((topicInfos) => !!topicInfos),
            tap(topicInfos => this.addTopic(topicInfos.topic, topicInfos.notify)),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    protected async onOpenCreatePollDialog(){

        this.pollSetupService.getTemplateBank().pipe(
            mergeMap(templateListByTopic => {
                const templates = templateListByTopic.templateListByTopics.flatMap(t => t.templates);
                return from(this.timelineItemDialogService.createPoll(this.teams, templates, this.author.fullname));
            }),
            mergeMap(pollDialogRef => {
                return pollDialogRef.beforeClosed().pipe(
                    filter((poll) => !!poll),
                    tap(async poll => {
                        await this.addPoll(poll);
                    }),
                    takeUntil(pollDialogRef.afterClosed())
                );
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    selectFilter(option: string){
        this.filterOption = option;
    }


    protected loadMore() {

        if (this.busy) return;

        this.busy = true;

        const start = this.dateRange.start;
        this.dateRange = new DateRange<Date>(addDays(start, -30), addDays(start, -1));

        this.timelineService.timeline(this.dateRange.start, this.dateRange.end, this.teamId).pipe(
            tap(timeline => {

                if (timeline.days.length > 0) {
                    //Concatenate the result to the existing array without changing its reference
                    Array.prototype.push.apply(this.days, timeline.days);
                } else {
                    this.noMoreItems = true;
                }
                this.busy = false;
            }),
            catchError(error => {
                        this.logger.error(error);
                        return EMPTY;
            }),
            takeUntil(this._destroy$)
        ).subscribe()
    }

    onRequestScrollToTimelineItem(target: string) {
        if (target) {
            this.viewportScroller.setOffset([0, 112]);
            this.viewportScroller.scrollToAnchor(target);
        }
    }

    scrollToTop() {
        this.viewportScroller.scrollToPosition([0,0]);
    }


    @HostListener('window:scroll')
    onScrollEvent() {
        if (window.scrollY > 800) {
            this.showScrollToTopBtn = "inline-block";
        } else {
            this.showScrollToTopBtn = "none";
        }

    }
}